import { Resolution } from './media.types';
import { SupportedFrameRate } from './types';

export enum Vendor {
  Vonage = 'Vonage',
}

/**
 * @enum Media access errors
 */
export enum MediaAccessError {
  PermissionDenied = 'PermissionDenied',
  PermissionDeniedBySystem = 'PermissionDeniedBySystem',
  DeviceNotFound = 'DeviceNotFound',
  IncorrectConstraints = 'IncorrectConstraints',
  NoVideoAudioConstraints = 'NoVideoAudioConstraints',
  Error = 'Error',
}

export enum MediaAccessStatus {
  Allowed = 'Allowed',
  Denied = 'Denied',
  Error = 'Error',
}

export enum FacingMode {
  User = 'user',
  Environment = 'environment',
}

export enum DeviceKind {
  VideoInput = 'videoinput',
  AudioInput = 'audioinput',
  AudioOutput = 'audiooutput',
}

export enum MediaTrackKind {
  Video = 'video',
  Audio = 'audio',
}

/**
 * Event types related to media updates
 */
export enum MediaEventType {
  /**
   * New permissions assigned
   */
  MediaAccessStatusChange = 'MediaAccessStatusChange',
  /**
   * Fired when video/audio is turned on/off
   */
  MediaStatusChange = 'MediaStatusChange',
  /**
   * Fired when a device is connected or disconnected
   */
  DevicesChange = 'DevicesChange',
  /**
   * Currently selected device changed, e.g. switched to the other camera
   */
  SelectedDevicesChange = 'SelectedDevicesChange',
  VideoElementUpdated = 'VideoElementUpdated',
  TracksAdded = 'TracksAdded',
  TracksRemoved = 'TracksRemoved',
  MediaStreamAudioTrackUpdated = 'MediaStreamAudioTrackUpdated',
  MediaStreamVideoTrackUpdated = 'MediaStreamVideoTrackUpdated',
  MediaStreamScreenTrackUpdated = 'MediaStreamScreenTrackUpdated',
  MediaStreamScreenTrackStarted = 'MediaStreamScreenTrackStarted',
  MediaStreamScreenTrackEnded = 'MediaStreamScreenTrackEnded',
  MediaSourceUpdated = 'MediaSourceUpdated',
  MediaAudioFallbackEnabled = 'MediaAudioFallbackEnabled',
  MediaAudioFallbackDisabled = 'MediaAudioFallbackDisabled',
  MediaVideoDisableWarningEnabled = 'MediaVideoDisableWarningEnabled',
  MediaVideoDisableWarningDisabled = 'MediaVideoDisableWarningDisabled',
  CaptionsPublishToggled = 'CaptionsPublishToggled',
  /**
   * Fired when media permissions are denied
   * Helps detect denial in browsers that don’t update permission status reliably
   */
  MediaAccessDenied = 'MediaAccessDenied',
}

export enum MediaStorageKeys {
  SelectedDevices = 'devicesInfo', // would be better '[media]selected-devices', but it is shared with angular app
}

export type VideoQualityConstraint = {
  frameRate: SupportedFrameRate;
  resolution: Resolution;
  bitrate: number;
};

export type VideoQuality = 'ld' | 'sd' | 'hd';

export const VideoQualityProfiles: Record<
  VideoQuality,
  VideoQualityConstraint
> = {
  ld: {
    frameRate: 15,
    resolution: '320x240',
    bitrate: 280_000,
  },
  sd: {
    frameRate: 30,
    resolution: '640x480',
    bitrate: 950_000,
  },
  hd: {
    frameRate: 30,
    resolution: '1280x720',
    bitrate: 2_500_000,
  },
} as const;

export const VideoQualityProfilesWide: Record<
  VideoQuality,
  VideoQualityConstraint
> = {
  ld: {
    frameRate: 15,
    resolution: '320x180',
    bitrate: 280_000,
  },
  sd: {
    frameRate: 30,
    resolution: '640x360',
    bitrate: 800_000,
  },
  hd: {
    frameRate: 30,
    resolution: '1280x720',
    bitrate: 2_500_000,
  },
} as const;
